// ResponsiveNavigation.jsx - モバイル対応ナビゲーションコンポーネント

import React from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, ArrowRight } from 'lucide-react';

/**
 * レスポンシブナビゲーションコンポーネント
 * NavigationBarのモバイル最適化版
 */
const ResponsiveNavigation = ({ 
  currentQuestionIndex, 
  totalQuestions, 
  freeNavigation, 
  sessionResponses, 
  questions,
  isTimeUp,
  quickAnswerMode = 'standard',
  onPrevious, 
  onNext, 
  onNavigateTo,
  onShowResults,
  enableResponsive = true  // 段階的移行用フラグ
}) => {
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;
  const hasCurrentAnswer = sessionResponses[questions[currentQuestionIndex]?.id];
  
  // 全問回答完了チェック
  const allAnswered = questions.length > 0 && questions.every(q => sessionResponses[q.id]);
  
  const canGoToNext = freeNavigation || hasCurrentAnswer || isTimeUp;

  if (!enableResponsive) {
    // 既存のNavigationBarと同じレイアウト（完全互換）
    return (
      <div className="space-y-4">
        {/* 問題インジケーター */}
        {freeNavigation && (
          <div className="flex flex-wrap gap-2 justify-center">
            {questions.map((_, index) => (
              <button
                key={index}
                onClick={() => onNavigateTo(index)}
                disabled={isTimeUp}
                className={`w-8 h-8 rounded-full flex items-center justify-center
                  ${currentQuestionIndex === index
                    ? 'bg-purple-600 text-white'
                    : sessionResponses[questions[index]?.id]
                      ? 'bg-purple-200 text-purple-800'
                      : 'bg-gray-200 text-gray-600'
                  } ${isTimeUp ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'} transition-opacity`}
                aria-label={`問題${index + 1}へ移動${isTimeUp ? '（時間切れのため無効）' : ''}`}
              >
                {index + 1}
              </button>
            ))}
          </div>
        )}

        {/* ナビゲーションボタン */}
        <div className="flex justify-between">
          <button
            onClick={onPrevious}
            disabled={isFirstQuestion || !freeNavigation || (isTimeUp && freeNavigation)}
            className={`flex items-center gap-2 px-4 py-2 rounded
              ${(isFirstQuestion || !freeNavigation || (isTimeUp && freeNavigation))
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'text-purple-600 hover:text-purple-800'}`}
            aria-label={`前の問題に戻るボタン${(isTimeUp && freeNavigation) ? '（時間切れのため無効）' : ''}`}
          >
            <ArrowLeft size={20} />
            前の問題
          </button>
          
          {quickAnswerMode !== 'quick' && (freeNavigation ? allAnswered : (isLastQuestion && hasCurrentAnswer)) ? (
            <button
              onClick={onShowResults}
              className="flex items-center gap-2 px-4 py-2 rounded bg-purple-600 text-white hover:bg-purple-700"
              aria-label="結果を表示するボタン"
            >
              結果を表示
              <ArrowRight size={20} />
            </button>
          ) : (
            <button
              onClick={onNext}
              disabled={isLastQuestion || !canGoToNext || (isTimeUp && freeNavigation)}
              className={`flex items-center gap-2 px-4 py-2 rounded
                ${(isLastQuestion || !canGoToNext || (isTimeUp && freeNavigation))
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'text-purple-600 hover:text-purple-800'}`}
              aria-label={`次の問題に進むボタン${(isTimeUp && freeNavigation) ? '（時間切れのため無効）' : ''}`}
            >
              次の問題
              <ArrowRight size={20} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // レスポンシブ改善版
  return (
    <div className="space-y-4">
      {/* 問題インジケーター - モバイル最適化 */}
      {freeNavigation && (
        <div className="flex flex-wrap gap-1 sm:gap-2 justify-center">
          {questions.map((_, index) => (
            <button
              key={index}
              onClick={() => onNavigateTo(index)}
              disabled={isTimeUp}
              className={`
                w-9 h-9 sm:w-8 sm:h-8 rounded-full flex items-center justify-center
                text-xs sm:text-sm font-medium
                ${currentQuestionIndex === index
                  ? 'bg-purple-600 text-white'
                  : sessionResponses[questions[index]?.id]
                    ? 'bg-purple-200 text-purple-800'
                    : 'bg-gray-200 text-gray-600'
                } ${isTimeUp ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80 active:scale-95'} 
                transition-all duration-150
                touch-manipulation
              `}
              aria-label={`問題${index + 1}へ移動${isTimeUp ? '（時間切れのため無効）' : ''}`}
              style={{ minWidth: '36px', minHeight: '36px' }} // タッチターゲット確保
            >
              {index + 1}
            </button>
          ))}
        </div>
      )}

      {/* ナビゲーションボタン - モバイル最適化 */}
      <div className="flex justify-between gap-2">
        <button
          onClick={onPrevious}
          disabled={isFirstQuestion || !freeNavigation || (isTimeUp && freeNavigation)}
          className={`
            flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2 rounded
            text-sm sm:text-base font-medium
            min-h-[44px] touch-manipulation
            ${(isFirstQuestion || !freeNavigation || (isTimeUp && freeNavigation))
              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
              : 'text-purple-600 hover:text-purple-800 active:bg-purple-50'} 
            transition-colors duration-150
          `}
          aria-label={`前の問題に戻るボタン${(isTimeUp && freeNavigation) ? '（時間切れのため無効）' : ''}`}
        >
          <ArrowLeft size={18} className="sm:size-5 flex-shrink-0" />
          <span className="hidden xs:inline sm:inline">前の問題</span>
          <span className="inline xs:hidden sm:hidden">前へ</span>
        </button>
        
        {quickAnswerMode !== 'quick' && (freeNavigation ? allAnswered : (isLastQuestion && hasCurrentAnswer)) ? (
          <button
            onClick={onShowResults}
            className="
              flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2 rounded 
              bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800
              text-sm sm:text-base font-medium
              min-h-[44px] touch-manipulation
              transition-colors duration-150
            "
            aria-label="結果を表示するボタン"
          >
            <span className="hidden xs:inline sm:inline">結果を表示</span>
            <span className="inline xs:hidden sm:hidden">結果</span>
            <ArrowRight size={18} className="sm:size-5 flex-shrink-0" />
          </button>
        ) : (
          <button
            onClick={onNext}
            disabled={isLastQuestion || !canGoToNext || (isTimeUp && freeNavigation)}
            className={`
              flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2 rounded
              text-sm sm:text-base font-medium
              min-h-[44px] touch-manipulation
              ${(isLastQuestion || !canGoToNext || (isTimeUp && freeNavigation))
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'text-purple-600 hover:text-purple-800 active:bg-purple-50'} 
              transition-colors duration-150
            `}
            aria-label={`次の問題に進むボタン${(isTimeUp && freeNavigation) ? '（時間切れのため無効）' : ''}`}
          >
            <span className="hidden xs:inline sm:inline">次の問題</span>
            <span className="inline xs:hidden sm:hidden">次へ</span>
            <ArrowRight size={18} className="sm:size-5 flex-shrink-0" />
          </button>
        )}
      </div>
    </div>
  );
};

ResponsiveNavigation.propTypes = {
  currentQuestionIndex: PropTypes.number.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  freeNavigation: PropTypes.bool.isRequired,
  sessionResponses: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  isTimeUp: PropTypes.bool,
  quickAnswerMode: PropTypes.string,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onNavigateTo: PropTypes.func.isRequired,
  onShowResults: PropTypes.func.isRequired,
  enableResponsive: PropTypes.bool
};

export default ResponsiveNavigation; 